/**
 * Music + ambient bed module for Horse Race. HTMLAudioElement streaming
 * routed through the shared AudioContext via createMediaElementSource so the
 * music gain still applies. Mirrors WhatIs audio/music.ts.
 *
 * Beds:
 *   lobby — looping in lobby scene
 *   race  — looping during BETTING phase (anticipation); fades during RACE
 *           so gallop+cheer dominate
 */

import lobbyBedUrl from '../assets/music/lobby-bed.mp3?url';
import raceBedUrl from '../assets/music/race-bed.mp3?url';

import { audioBus } from './bus.js';

export type MusicName = 'lobby' | 'race';

interface MusicSpec { url: string; loop: boolean }

const SPEC_LOBBY: MusicSpec = { url: lobbyBedUrl, loop: true };
const SPEC_RACE: MusicSpec = { url: raceBedUrl, loop: true };

function specOf(name: MusicName): MusicSpec {
  if (name === 'lobby') return SPEC_LOBBY;
  return SPEC_RACE;
}

interface Track {
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
}

const tracks = new Map<MusicName, Track>();
let current: { name: MusicName; track: Track } | null = null;

function getTrack(name: MusicName): Track {
  let t = tracks.get(name);
  if (t) return t;
  const spec = specOf(name);
  const el = new Audio(spec.url);
  el.preload = 'auto';
  el.loop = spec.loop;
  let source: MediaElementAudioSourceNode | null = null;
  const ctx = audioBus.context();
  if (ctx) {
    try {
      source = ctx.createMediaElementSource(el);
      source.connect(audioBus.musicGain());
    } catch (err) {
      console.warn(`[hr-music] media-element-source failed for ${name}:`, err);
    }
  }
  t = { el, source };
  tracks.set(name, t);
  return t;
}

export const music = {
  play(name: MusicName): void {
    if (current && current.name === name && !current.track.el.paused) return;
    if (current) {
      try { current.track.el.pause(); } catch { /* ignore */ }
    }
    const track = getTrack(name);
    current = { name, track };
    if (!audioBus.isUnlocked()) {
      void audioBus.unlock();
    }
    try {
      track.el.currentTime = 0;
      const p = track.el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          console.debug(`[hr-music] autoplay blocked for ${name}:`, err);
        });
      }
    } catch (err) {
      console.warn(`[hr-music] play ${name} failed:`, err);
    }
  },
  stop(): void {
    if (!current) return;
    try { current.track.el.pause(); } catch { /* ignore */ }
    current = null;
  },
  /** Fade music volume over `ms` (uses HTMLAudioElement.volume linear ramp). */
  fadeOut(ms = 600): void {
    if (!current) return;
    const el = current.track.el;
    const start = el.volume;
    const steps = 12;
    const interval = Math.max(20, Math.floor(ms / steps));
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      el.volume = Math.max(0, start * (1 - i / steps));
      if (i >= steps) {
        clearInterval(id);
        try { el.pause(); } catch { /* ignore */ }
        el.volume = start;
        current = null;
      }
    }, interval);
  },
  resumeIfPending(): void {
    if (!current) return;
    const el = current.track.el;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* still blocked, ignore */ });
      }
    }
  },
};
